import { t } from "@lingui/macro";
import {
  BillingPlanBudgetCacheStatusEnum,
  type BillingPlanCacheOrderByColumnEnum,
  BillingPlanCacheOverviewWhereColumn,
  CreateInvoiceFromBillingPlanDocument,
  type CreateInvoiceFromBillingPlanMutation,
  type CreateInvoiceFromBillingPlanMutationVariables,
  type InvoicingPlanAdvancedListFragment,
  InvoicingPlanFutureListDocument,
  InvoicingPlanFutureListFilterOptionsDocument,
  type InvoicingPlanFutureListFilterOptionsQuery,
  type InvoicingPlanFutureListFilterOptionsQueryVariables,
  type InvoicingPlanFutureListQuery,
  type InvoicingPlanFutureListQueryVariables,
  InvoicingPlanFutureListSummaryDocument,
  type InvoicingPlanFutureListSummaryQuery,
  type InvoicingPlanFutureListSummaryQueryVariables,
  type SortOrder,
  SqlOperator,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import type { PipedriveStore } from "@src/stores/pipedrive";
import { commonSearchParams } from "@src/utils/apolloHelpers";
import { getPaymentStatusFilterOptions } from "@src/utils/components/filters/constants";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { now, toApiDate } from "@src/utils/dates";
import mapToOptions from "@src/utils/map-to-options";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import type { DateRange } from "@src/utils/types";
import { endOfMonth, startOfMonth } from "date-fns";
import { action, computed, makeObservable, observable } from "mobx";
import Router from "next/router";

export class InvoicingPlanAutomatedListStore {
  readonly tableKey = "invoicing-plan-automated-list";

  @observable.ref
  searchTerm = "";

  @observable.ref
  tableData: InvoicingPlanAdvancedListFragment[] = [];

  @observable.ref
  summary:
    | InvoicingPlanFutureListSummaryQuery["billingPlanCacheOverviewSummary"]
    | undefined = undefined;

  @observable.ref
  dateRange: DateRange = {
    start: startOfMonth(now()),
    end: endOfMonth(now()),
  };

  pagination = new PaginationState(this.tableKey, {
    onChangePagination: () => {
      this.fetchTableData();
    },
  });
  orderBy = new OrderBy<BillingPlanCacheOrderByColumnEnum>();
  where = new Filters<BillingPlanCacheOverviewWhereColumn>(
    [
      new Filter({
        title: t`Client/Brand`,
        column: BillingPlanCacheOverviewWhereColumn.BrandId,
        operator: SqlOperator.In,
        tagTitle: t`Brand`,
        options: [],
        sectioned: true,
        hasSelectAllOption: true,
      }),
      new Filter({
        title: t`Project`,
        column: BillingPlanCacheOverviewWhereColumn.ProjectId,
        operator: SqlOperator.In,
        options: [],
      }),
      new Filter({
        title: t`Team`,
        column: BillingPlanCacheOverviewWhereColumn.TeamId,
        operator: SqlOperator.In,
        options: [],
      }),
      new Filter({
        title: t`Project Manager`,
        column: BillingPlanCacheOverviewWhereColumn.ProjectMangerId,
        operator: SqlOperator.In,
        options: [],
      }),
      new Filter({
        title: t`Project Stage`,
        column: BillingPlanCacheOverviewWhereColumn.ProjectStageId,
        operator: SqlOperator.In,
        options: [],
      }),
      new Filter({
        title: t`Status`,
        column: BillingPlanCacheOverviewWhereColumn.Status,
        operator: SqlOperator.In,
        options: [
          {
            label: t`Created`,
            value: BillingPlanBudgetCacheStatusEnum.Created,
          },
          {
            label: t`Manually created`,
            value: BillingPlanBudgetCacheStatusEnum.ManuallyCreated,
          },
          {
            label: t`Pending`,
            value: BillingPlanBudgetCacheStatusEnum.Pending,
          },
          {
            label: t`Draft`,
            value: BillingPlanBudgetCacheStatusEnum.Draft,
          },
          {
            label: t`Failed`,
            value: BillingPlanBudgetCacheStatusEnum.Failed,
          },
          {
            label: t`Deleted`,
            value: BillingPlanBudgetCacheStatusEnum.Deleted,
          },
        ],
      }),
      new Filter({
        title: t`Date of Supply`,
        column: BillingPlanCacheOverviewWhereColumn.DateOfSupply,
        operator: SqlOperator.Between,
        dateRange: true,
        options: [],
      }),
      new Filter({
        title: t`Issue Date`,
        column: BillingPlanCacheOverviewWhereColumn.IssueDate,
        operator: SqlOperator.Between,
        dateRange: true,
        options: [],
      }),
      new Filter({
        title: t`Due Date`,
        column: BillingPlanCacheOverviewWhereColumn.DueDate,
        operator: SqlOperator.Between,
        dateRange: true,
        options: [],
      }),
      new Filter({
        title: t`Paid At`,
        column: BillingPlanCacheOverviewWhereColumn.PaidAt,
        operator: SqlOperator.Between,
        dateRange: true,
        options: [],
      }),
      new Filter({
        title: t`Payment status`,
        column: BillingPlanCacheOverviewWhereColumn.PaymentStatus,
        operator: SqlOperator.In,
        options: getPaymentStatusFilterOptions(),
      }),
    ],
    {
      onChange: () => {
        this.refetchData();
      },
      tableKey: this.tableKey,
      orderBy: this.orderBy,
    },
  );

  private fetcherTableData = new FetchHelper<
    InvoicingPlanFutureListQuery,
    InvoicingPlanFutureListQueryVariables
  >(InvoicingPlanFutureListDocument);
  private fetcherSummary = new FetchHelper<
    InvoicingPlanFutureListSummaryQuery,
    InvoicingPlanFutureListSummaryQueryVariables
  >(InvoicingPlanFutureListSummaryDocument);
  private fetcherFilterOptions = new FetchHelper<
    InvoicingPlanFutureListFilterOptionsQuery,
    InvoicingPlanFutureListFilterOptionsQueryVariables
  >(InvoicingPlanFutureListFilterOptionsDocument);
  private createInvoiceMutator = new MutationHelper<
    CreateInvoiceFromBillingPlanMutation,
    CreateInvoiceFromBillingPlanMutationVariables
  >(CreateInvoiceFromBillingPlanDocument);

  @observable
  rowCreateLoadingIds = new Set<string>();

  constructor(appStore: AppStore) {
    makeObservable(this);
    const projectStateFilter = this.where.filtersByColumn.get(
      BillingPlanCacheOverviewWhereColumn.ProjectStageId,
    );

    this.fetchFilterOptions();
    if (projectStateFilter) {
      projectStateFilter.hidden =
        !appStore.workspaceStore.settings?.pipedriveEnabled;
      this.setProjectStageOptions(appStore.pipedriveStore);
    }

    this.where.fromURLSearchParam(Router.query, {
      skipOnChange: true,
    });
    this.orderBy.fromURLSearchParam(Router.query);

    if ("search" in Router.query && typeof Router.query.search === "string") {
      this.searchTerm = Router.query.search;
    }

    if (
      "date_from" in Router.query &&
      typeof Router.query.date_from === "string" &&
      "date_to" in Router.query &&
      typeof Router.query.date_to === "string"
    ) {
      this.dateRange = {
        start: new Date(Router.query.date_from),
        end: new Date(Router.query.date_to),
      };
    }

    this.pagination.fromURLSearchParam(Router.query);
    this.fetchSummary();
  }

  @computed
  get searchParamsExtended() {
    return {
      ...commonSearchParams(this),
      date_from: toApiDate(this.dateRange.start),
      date_to: toApiDate(this.dateRange.end),
    };
  }

  @action.bound
  onSearch(value: string) {
    this.searchTerm = value;
    this.refetchData();
  }

  @action.bound
  async setProjectStageOptions(pipedriveStore: PipedriveStore) {
    await pipedriveStore.fetchOptions();
    this.where.filtersByColumn
      .get(BillingPlanCacheOverviewWhereColumn.ProjectStageId)
      ?.setOptions(pipedriveStore.projectStageOptions);
  }

  @action.bound
  setDateRange(dateRange: DateRange) {
    this.dateRange = dateRange;
    this.refetchData();
  }

  @computed
  get isSummaryLoading(): boolean {
    return this.fetcherSummary.isLoading.value;
  }

  @action.bound
  private async fetchSummary() {
    const [data, error] = await this.fetcherSummary.fetch({
      filters: {
        where: this.where.asWhereParam,
        search: this.searchTerm,
        date_from: toApiDate(this.dateRange.start),
        date_to: toApiDate(this.dateRange.end),
      },
    });

    if (error) {
      this.summary = undefined;
      return;
    }

    this.summary = data.billingPlanCacheOverviewSummary;
  }

  @computed
  get isTableDataLoading(): boolean {
    return this.fetcherTableData.isLoading.value;
  }

  @action.bound
  private async fetchTableData() {
    const [data, error] = await this.fetcherTableData.fetch({
      filters: {
        where: this.where.asWhereParam,
        orderBy: this.orderBy.asWhereParam,
        search: this.searchTerm,
        date_from: toApiDate(this.dateRange.start),
        date_to: toApiDate(this.dateRange.end),
      },
      first: this.pagination.asParams.first,
      page: this.pagination.asParams.page,
    });

    if (error || !data.billingPlanCacheOverview) {
      this.tableData = [];
      return;
    }

    this.tableData = data.billingPlanCacheOverview.data;
    this.pagination.setFromPaginatorInfo(
      data.billingPlanCacheOverview.paginatorInfo,
    );
  }

  @action.bound
  private refetchData() {
    this.pagination.resetPage();
    this.fetchSummary();
  }

  @action.bound
  handleChangeOrderBy(clauses: {
    column: BillingPlanCacheOrderByColumnEnum;
    order: SortOrder | undefined;
  }) {
    if (!clauses.order) {
      this.orderBy.setClauses([]);
      this.pagination.resetPage();
      return;
    }
    this.orderBy.setClauses([{ column: clauses.column, order: clauses.order }]);
    this.pagination.resetPage();
  }

  @action.bound
  async fetchFilterOptions() {
    const [data, error] = await this.fetcherFilterOptions.fetch();

    if (error) return;

    this.where.filtersByColumn
      .get(BillingPlanCacheOverviewWhereColumn.ProjectId)
      ?.setOptions(mapToOptions.projects(data.projectsSimpleMap));
    this.where.filtersByColumn
      .get(BillingPlanCacheOverviewWhereColumn.TeamId)
      ?.setOptions(mapToOptions.teams(data.teamSimpleMap));
    this.where.filtersByColumn
      .get(BillingPlanCacheOverviewWhereColumn.ProjectMangerId)
      ?.setOptions(
        mapToOptions.projectManagers.toOptions(data.projectManagerSimpleMap),
      );
    this.where.filtersByColumn
      .get(BillingPlanCacheOverviewWhereColumn.BrandId)
      ?.setOptions(mapToOptions.brandsSectioned(data.clientsSimpleMap));
  }

  @action.bound
  async createInvoiceForRow(
    row: InvoicingPlanAdvancedListFragment,
  ): Promise<void> {
    this.rowCreateLoadingIds.add(row.id);

    const [data, error] = await this.createInvoiceMutator.mutate({
      input: {
        project_id: row.project.id,
        issue_date: row.issue_date,
      },
    });

    this.rowCreateLoadingIds.delete(row.id);

    if (error) return;

    window.open(
      `/invoices/for-invoicing/edit?id=${data.createInvoiceFromBillingPlan.id}`,
      "_blank",
    );
  }
}
